import type { SubscriptionInfo } from "./api";

const SNAPSHOT_KEY = "ces.checkout.before";

export type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** What the subscription looked like — enough to tell a completed payment from
 *  a webhook that hasn't landed yet. */
export type BillingSnapshot = {
  planTier: string;
  credits: number;
};

export function billingSnapshot(
  subscription: SubscriptionInfo | null | undefined,
): BillingSnapshot {
  return {
    planTier: subscription?.subscription.plan_tier ?? "",
    credits: subscription?.current_usage.additional_scans_credit ?? 0,
  };
}

/**
 * Has the backend applied the payment yet? A plan purchase moves `planTier`, a
 * scan-pack purchase moves `credits` and leaves the tier alone — so both are
 * compared. With no "before" snapshot (user opened the success URL directly, or
 * storage is unavailable) we report changed, so the UI settles instead of
 * polling for something it can never detect.
 */
export function billingChanged(
  before: BillingSnapshot | null,
  after: BillingSnapshot,
): boolean {
  if (!before) return true;
  return before.planTier !== after.planTier || before.credits !== after.credits;
}

/** What the settings page should do for a given landing.
 *
 *  - `poll` — provider says success and we have a "before" snapshot: wait for
 *    the backend to apply the payment.
 *  - `settle` — success claimed but there is nothing to compare against (manual
 *    visit to the return URL, or storage was unavailable): refresh the data
 *    quietly and do NOT announce a payment we cannot verify.
 *  - `cancelled` — the provider sent the user to the cancel URL, or a snapshot
 *    is still lying around with no return parameter at all — that's a checkout
 *    abandoned via the browser Back button.
 *  - `none` — a plain visit to the page.
 */
export function classifyCheckoutReturn(
  param: string | null,
  snapshot: BillingSnapshot | null,
): "poll" | "settle" | "cancelled" | "none" {
  if (param === "success") return snapshot ? "poll" : "settle";
  if (param === "cancel") return "cancelled";
  return snapshot ? "cancelled" : "none";
}

function defaultStorage(): StorageLike | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    // Private mode / blocked storage — callers degrade to "no snapshot".
    return null;
  }
}

/** Stash the pre-payment state before handing the user to the provider. */
export function rememberBillingSnapshot(
  snapshot: BillingSnapshot,
  storage: StorageLike | null = defaultStorage(),
): void {
  try {
    storage?.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    /* quota or disabled storage — the return flow copes without it */
  }
}

/**
 * Read the stashed state without consuming it. Reading and clearing must stay
 * separate: React re-runs effects in development, and a read that also cleared
 * would leave the second run with no baseline to compare against.
 */
export function readBillingSnapshot(
  storage: StorageLike | null = defaultStorage(),
): BillingSnapshot | null {
  try {
    const raw = storage?.getItem(SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const { planTier, credits } = parsed as Partial<BillingSnapshot>;
    if (typeof planTier !== "string" || typeof credits !== "number") return null;
    return { planTier, credits };
  } catch {
    return null;
  }
}

/** Drop the stashed state once the return flow has settled, so a later reload
 *  doesn't re-arm a wait for a change that already happened. */
export function clearBillingSnapshot(
  storage: StorageLike | null = defaultStorage(),
): void {
  try {
    storage?.removeItem(SNAPSHOT_KEY);
  } catch {
    /* nothing to do — a stale snapshot only costs one redundant poll */
  }
}
